/**
 * Affichage d'un résumé généré — design Carbon
 */

import { useState } from 'react'
import { Tile, Layer, Tag, Button } from '@carbon/react'
import {
  Copy,
  Download,
  Document,
  Renew,
  CheckmarkFilled,
  Time,
  Globe,
} from '@carbon/icons-react'
import type { SummarySchema } from '@sphere/ai/domain'
import styles from './SummaryDisplay.module.scss'

interface SummaryDisplayProps {
  summary: SummarySchema
  onDownload?: () => void
  onNewSummary?: () => void
  className?: string
}

export function SummaryDisplay({
  summary,
  onDownload,
  onNewSummary,
  className = '',
}: SummaryDisplayProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      const fullText = `${summary.title}\n\n${summary.summary}\n\nPoints clés:\n${summary.key_points.map(p => `• ${p}`).join('\n')}`
      await navigator.clipboard.writeText(fullText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  }

  const reduction = summary.word_count.reduction_percentage
  const reductionClass =
    reduction >= 80
      ? styles.statGood
      : reduction >= 60
        ? styles.statMedium
        : styles.statLow

  return (
    <Layer className={`${styles.card} ${className}`}>
      <Tile className={styles.tile}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{summary.title}</h1>
            <div className={styles.actions}>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={copied ? CheckmarkFilled : Copy}
                onClick={handleCopy}
              >
                {copied ? 'Copié !' : 'Copier'}
              </Button>
              {onDownload && (
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Download}
                  onClick={onDownload}
                >
                  Télécharger
                </Button>
              )}
            </div>
          </div>

          <div className={styles.tags}>
            {summary.metadata?.language && (
              <Tag type="outline" size="sm">
                <Globe size={16} className={styles.tagIcon} />
                {summary.metadata.language.toUpperCase()}
              </Tag>
            )}
            {summary.metadata?.reading_time && (
              <Tag type="outline" size="sm">
                <Time size={16} className={styles.tagIcon} />
                {summary.metadata.reading_time} min de lecture
              </Tag>
            )}
            {summary.metadata?.topic && (
              <Tag type="outline" size="sm">
                <Document size={16} className={styles.tagIcon} />
                {summary.metadata.topic}
              </Tag>
            )}
          </div>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {summary.word_count.original.toLocaleString()}
            </span>
            <span className={styles.statLabel}>Mots original</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {summary.word_count.summary.toLocaleString()}
            </span>
            <span className={styles.statLabel}>Mots résumé</span>
          </div>
          <div className={`${styles.stat} ${reductionClass}`}>
            <span className={styles.statValue}>
              {summary.word_count.reduction_percentage}%
            </span>
            <span className={styles.statLabel}>Réduction</span>
          </div>
        </div>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Résumé</h2>
          <p className={styles.summaryText}>{summary.summary}</p>
        </section>

        {summary.key_points?.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Points clés</h2>
            <ul className={styles.keyPoints}>
              {summary.key_points.map((point, i) => (
                <li key={i} className={styles.keyPoint}>
                  <span className={styles.keyPointNum}>{i + 1}</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className={styles.footer}>
          <Button kind="tertiary" renderIcon={Copy} onClick={handleCopy}>
            Copier le résumé
          </Button>
          {onDownload && (
            <Button kind="tertiary" renderIcon={Download} onClick={onDownload}>
              Exporter
            </Button>
          )}
          {onNewSummary && (
            <Button kind="primary" renderIcon={Renew} onClick={onNewSummary}>
              Nouveau résumé
            </Button>
          )}
        </div>
      </Tile>
    </Layer>
  )
}
